import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
} from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  nextAvailableTimeSeriesName,
  type TimeSeries,
  type TimeSeriesPoint,
} from "../model/inp/timeseries.ts";
import { parseClipboardGrid } from "../model/clipboardGrid.ts";
import { handleGridArrowNav, handleGridDeleteRow } from "./gridKeyboardNav.ts";
import { NumberInput } from "./fields.tsx";
import { useT } from "../i18n/hooks.ts";
import { PlusIcon } from "./PlusIcon.tsx";
import { NamedCollectionList } from "./NamedCollectionList.tsx";
import { NamedCollectionHeader } from "./NamedCollectionHeader.tsx";
import { useNamedCollection } from "./useNamedCollection.ts";
import { VirtualList } from "./VirtualList.tsx";
import {
  CHART_AXIS_TICK,
  CHART_GRID_PROPS,
  CHART_SERIES_PRIMARY,
  CHART_TOOLTIP_STYLE,
} from "./chartTheme.ts";

/** A real `[TIMESERIES]` (a rainfall hyetograph, say) routinely has thousands
 * to tens of thousands of points — this table used to mount one real `<tr>`
 * per point up front regardless, freezing the tab on open, and every field's
 * `onChange` copied the WHOLE points array on every keystroke (P1 deep-
 * review). VirtualList.tsx measures its own row height automatically (every
 * row here has the same 3-input shape, so one measurement covers all of
 * them) — only the visible list's own fixed pixel height is needed here. */
const POINTS_LIST_HEIGHT = 260;

/**
 * One point row — date/time/value are buffered LOCALLY (not read from
 * `point` on every keystroke) and only reach `onCommit` (which pushes
 * through `updateSelected`'s own O(n) points-array copy) on blur, not on
 * every character typed. This is what actually fixes the per-keystroke
 * copy cost above VirtualList's own windowing (which only reduces how many
 * ROWS exist as DOM nodes, not how often the array itself gets copied).
 */
function TimeSeriesPointRow({
  point,
  index,
  onCommit,
  onPaste,
  onKeyDown,
}: {
  point: TimeSeriesPoint;
  index: number;
  onCommit: (patch: Partial<TimeSeriesPoint>) => void;
  onPaste: (e: ClipboardEvent<HTMLInputElement>, col: 0 | 1 | 2) => void;
  onKeyDown: (e: KeyboardEvent<HTMLInputElement>) => void;
}) {
  const [date, setDate] = useState(point.date);
  const [time, setTime] = useState(point.time);
  const [value, setValue] = useState(point.value);
  useEffect(() => setDate(point.date), [point.date]);
  useEffect(() => setTime(point.time), [point.time]);
  useEffect(() => setValue(point.value), [point.value]);
  return (
    <div className="timeseries-grid-row" role="row" data-index={index}>
      <div className="timeseries-grid-cell" data-grid-cell role="gridcell">
        <input
          type="text"
          value={date}
          onChange={(e) => setDate(e.target.value)}
          onBlur={() => onCommit({ date })}
          onPaste={(e) => onPaste(e, 0)}
          onKeyDown={onKeyDown}
        />
      </div>
      <div className="timeseries-grid-cell" data-grid-cell role="gridcell">
        <input
          type="text"
          value={time}
          onChange={(e) => setTime(e.target.value)}
          onBlur={() => onCommit({ time })}
          onPaste={(e) => onPaste(e, 1)}
          onKeyDown={onKeyDown}
        />
      </div>
      <div className="timeseries-grid-cell" data-grid-cell role="gridcell">
        <NumberInput
          value={value}
          onChange={setValue}
          onBlur={() => onCommit({ value })}
          onPaste={(e) => onPaste(e, 2)}
          onKeyDown={onKeyDown}
        />
      </div>
    </div>
  );
}

export function TimeSeriesEditor({
  series,
  onChange,
  selectedName,
  onSelect,
}: {
  series: TimeSeries[];
  onChange: (series: TimeSeries[]) => void;
  selectedName: string | null;
  onSelect: (name: string | null) => void;
}) {
  const t = useT();
  const {
    selected,
    add,
    remove: deleteSeries,
    rename: renameSelected,
    update: updateSelected,
  } = useNamedCollection(series, onChange, selectedName, onSelect);
  // Rebuilding this by hand on every render was another O(n) copy on every
  // keystroke (same root cause as the points-table fix above) — memoized on
  // the points array itself so a keystroke in an unrelated field (a rename,
  // say) doesn't also re-copy the whole chart data.
  const chartData = useMemo(
    () => selected?.points.map((p, i) => ({ ...p, index: i })) ?? [],
    [selected]
  );

  // A stable per-row key for VirtualList, since a raw `TimeSeriesPoint` has
  // no natural identity of its own (index-based keys misattribute a row's
  // own focus after a mid-list insert/delete). `series`/`points` are always
  // freshly re-derived from `rawSections` (this codebase's own convention —
  // see `App.tsx`'s `project` memo for the general rationale), so an id
  // embedded IN a `TimeSeriesPoint` would just get reassigned on every
  // derivation, no more stable than the index itself. This ref instead
  // tracks ids alongside the points array, updated in lockstep by every
  // mutation below (add/delete/paste keep both in sync) — a length
  // mismatch (a series switch, or an external change like undo/redo this
  // component didn't itself cause) falls back to a fresh set, same as a
  // first mount.
  const pointIdsRef = useRef<string[]>([]);
  const nextPointIdRef = useRef(0);
  if (pointIdsRef.current.length !== (selected?.points.length ?? 0)) {
    pointIdsRef.current = (selected?.points ?? []).map(() => `p${nextPointIdRef.current++}`);
  }

  function addSeries() {
    const name = nextAvailableTimeSeriesName(new Set(series.map((s) => s.name)));
    add({ name, points: [{ date: "", time: "0", value: 0 }] });
  }

  function removePoint(index: number) {
    updateSelected((s) => {
      if (s.points.length <= 1) return s;
      pointIdsRef.current = pointIdsRef.current.filter((_, j) => j !== index);
      return { ...s, points: s.points.filter((_, j) => j !== index) };
    });
  }

  // A row past the last existing point is appended rather than dropped.
  function handlePointsPaste(
    e: ClipboardEvent<HTMLInputElement>,
    rowIndex: number,
    colIndex: 0 | 1 | 2
  ) {
    e.preventDefault();
    const grid = parseClipboardGrid(e.clipboardData.getData("text"));
    updateSelected((s) => {
      const points: TimeSeriesPoint[] = [...s.points];
      const ids = [...pointIdsRef.current];
      grid.forEach((row, r) => {
        const targetRow = rowIndex + r;
        while (points.length <= targetRow) {
          points.push({ date: "", time: "0", value: 0 });
          ids.push(`p${nextPointIdRef.current++}`);
        }
        row.forEach((cell, cOffset) => {
          const col = colIndex + cOffset;
          if (col > 2) return;
          if (col === 0) points[targetRow] = { ...points[targetRow], date: cell };
          else if (col === 1) points[targetRow] = { ...points[targetRow], time: cell };
          else {
            const value = Number(cell);
            if (!Number.isNaN(value)) points[targetRow] = { ...points[targetRow], value };
          }
        });
      });
      pointIdsRef.current = ids;
      return { ...s, points };
    });
  }

  return (
    <div className="named-collection-editor">
      <NamedCollectionList
        items={series}
        selectedName={selectedName}
        onSelect={onSelect}
        onAdd={addSeries}
        addLabel={t("projectData.addTimeSeries")}
      />
      <div className="named-collection-detail">
        {!selected && (
          <p className="property-panel-hint">{t("projectData.noTimeSeriesSelected")}</p>
        )}
        {selected && (
          <>
            <NamedCollectionHeader
              name={selected.name}
              onRename={renameSelected}
              onDelete={() => deleteSeries(selected.name)}
              deleteLabel={t("projectData.deleteTimeSeries")}
            />
            <div className="curve-editor-body">
              <div className="curve-editor-table">
                <span className="curve-editor-section-title">{t("projectData.valuesTable")}</span>
                <div className="timeseries-grid" role="table">
                  <div className="timeseries-grid-header" role="row">
                    <div role="columnheader">{t("projectData.date")}</div>
                    <div role="columnheader">{t("projectData.time")}</div>
                    <div role="columnheader">{t("projectData.value")}</div>
                  </div>
                  <VirtualList
                    items={selected.points}
                    height={POINTS_LIST_HEIGHT}
                    className="timeseries-grid-body"
                    getKey={(_point, i) => pointIdsRef.current[i] ?? i}
                    renderItem={(point, i) => (
                      <TimeSeriesPointRow
                        point={point}
                        index={i}
                        onCommit={(patch) =>
                          updateSelected((s) => ({
                            ...s,
                            points: s.points.map((p, j) => (j === i ? { ...p, ...patch } : p)),
                          }))
                        }
                        onPaste={(e, col) => handlePointsPaste(e, i, col)}
                        onKeyDown={(e) => {
                          handleGridArrowNav(e);
                          handleGridDeleteRow(e, () => removePoint(i));
                        }}
                      />
                    )}
                  />
                </div>
                <button
                  className="button"
                  onClick={() =>
                    updateSelected((s) => {
                      pointIdsRef.current = [
                        ...pointIdsRef.current,
                        `p${nextPointIdRef.current++}`,
                      ];
                      return { ...s, points: [...s.points, { date: "", time: "0", value: 0 }] };
                    })
                  }
                >
                  <PlusIcon />
                  {t("projectData.addPoint")}
                </button>
              </div>
              <div className="curve-editor-chart">
                <span className="curve-editor-section-title">{t("projectData.chartPreview")}</span>
                <ResponsiveContainer width="100%" height={220}>
                  <LineChart data={chartData} margin={{ top: 4, right: 12, bottom: 0, left: -12 }}>
                    <CartesianGrid {...CHART_GRID_PROPS} />
                    <XAxis
                      dataKey="index" // Time is free text, not a reliable numeric axis
                      type="number"
                      domain={["dataMin", "dataMax"]}
                      tick={CHART_AXIS_TICK}
                    />
                    <YAxis dataKey="value" tick={CHART_AXIS_TICK} />
                    <Tooltip
                      labelFormatter={(label) => selected.points[Number(label)]?.time ?? label}
                      contentStyle={CHART_TOOLTIP_STYLE}
                    />
                    <Line
                      type="linear"
                      dataKey="value"
                      stroke={CHART_SERIES_PRIMARY}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
